import { useEffect, useState, type MouseEvent } from 'react';

import { getLocale, t, useT, type MessageKey } from '@/i18n';
import { copyText } from '@/lib/clipboard';
import { formatBytes } from '@/lib/format';
import { MASK_TEXT } from '@/lib/secretMask';
import { useMessageContent } from '@/hooks/useMessageContent';

import styles from './chat.module.css';
import { DecryptText } from './DecryptText';
import { Avatar } from './Avatar';
import { CheckIcon, CopyIcon, DownloadIcon, EyeIcon, EyeOffIcon, FileIcon, TrashIcon } from './Icons';
import { useToast } from './Toast';
import { percent, useFileDownload } from './useFileDownload';
import type { ChatMessageProps, ChatSide } from './ChatMessage.types';

/** Who wrote a message, as the viewer sees it: themselves, the guest, the host, or their own other device. */
export type { ChatSide } from './ChatMessage.types';

const SIDE_NAMES: Record<ChatSide, MessageKey> = { me: 'chatMessage.sideMe', guest: 'chatMessage.sideGuest', host: 'chatMessage.sideHost', device: 'chatMessage.sideDevice' };

export function sideName(side: ChatSide): string {
  return t(SIDE_NAMES[side]);
}

const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(getLocale().intl, TIME_FORMAT);
}

/** The time without AM/PM (or its place in the language), for the gutter beside a grouped message. */
function shortTimeOf(iso: string): string {
  return new Intl.DateTimeFormat(getLocale().intl, TIME_FORMAT)
    .formatToParts(new Date(iso))
    .filter((part) => part.type !== 'dayPeriod')
    .map((part) => part.value)
    .join('')
    .trim();
}

/**
 * One message in the chat, laid out like a Slack message: avatar, name and
 * time, then the text or a file card. Actions (show, copy, delete) float in
 * a small toolbar on hover, on keyboard focus, or after a tap on a phone.
 * Every text is shown; the chat's switch (or each message's own) masks it.
 */
export function ChatMessage({ message, bridgeId, bridgeKey, fetchChunk, side, avatarSeed, grouped, canDelete, onDelete, online, hidden = false }: ChatMessageProps): React.JSX.Element {
  useT();
  const toast = useToast();
  const content = useMessageContent(message, bridgeKey, bridgeId);
  // Every message is shown, yours and theirs alike; the chat's switch masks or shows them all at once
  // A choice made on this message holds only while the chat's switch stays where it was; moving the switch wins again
  const [choice, setChoice] = useState<{ readonly whenHidden: boolean; readonly revealed: boolean } | null>(null);
  const revealed = choice?.whenHidden === hidden ? choice.revealed : !hidden;
  const [active, setActive] = useState(false);
  const file = useFileDownload(message.kind === 'file' ? message : null, content.kind === 'file' ? content.header : null, bridgeKey, fetchChunk);

  // Copying answers right at the button — the icon turns into a green check for a moment — not in a toast across the page
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);
  const copy = async (): Promise<void> => {
    if (content.kind !== 'text') return;
    if (await copyText(content.text)) setCopied(true);
    else toast.error(t('chatMessage.copyFailed'));
  };

  // A tap on the message (not on one of its buttons) shows its actions on touch screens.
  const toggleActive = (event: MouseEvent<HTMLElement>): void => {
    if (event.target instanceof Element && event.target.closest('button, a')) return;
    setActive((a) => !a);
  };

  const name = sideName(side);
  const time = timeOf(message.createdAt);
  const masked = content.kind === 'text' ? MASK_TEXT : '';
  const hasTools = content.kind === 'text' || canDelete;

  return (
    <article
      className={[styles.msg, grouped ? styles.msgGrouped : '', active ? styles.msgActive : ''].join(' ')}
      aria-label={t(message.kind === 'file' ? 'chatMessage.labelFile' : 'chatMessage.labelMessage', { name, time })}
      data-testid="message"
      data-side={side}
      onClick={toggleActive}
    >
      {grouped ? (
        <time className={styles.gutterTime} dateTime={message.createdAt}>
          {shortTimeOf(message.createdAt)}
        </time>
      ) : (
        <span className={styles.who}>
          {/* every side has its face, your other device too, so both screens show the same two faces */}
          <Avatar seed={avatarSeed} />
          {online === undefined ? null : <i className={styles.presenceDot} data-online={online} aria-hidden="true" data-testid="presence-dot" />}
        </span>
      )}

      <div className={styles.msgMain}>
        {grouped ? null : (
          <div className={styles.msgHead}>
            <span className={styles.msgName}>{name}</span>
            <time className={styles.msgTime} dateTime={message.createdAt}>
              {time}
            </time>
          </div>
        )}

        {content.kind === 'pending' ? <p className={styles.msgNote}>{t('chatMessage.decrypting')}</p> : null}
        {content.kind === 'error' ? <p className={[styles.msgNote, styles.msgError].join(' ')}>{content.reason === 'version' ? t('chatMessage.errorVersion') : t('chatMessage.errorDecrypt')}</p> : null}
        {content.kind === 'text' ? (
          <p className={[styles.msgText, revealed ? '' : styles.masked].join(' ')} data-testid="message-text" title={revealed ? undefined : t('chatMessage.hidden')}>
            {revealed ? <DecryptText text={content.text} /> : masked}
          </p>
        ) : null}
        {content.kind === 'file' ? (
          <div className={styles.file}>
            <span className={styles.fileIcon} aria-hidden="true">
              <FileIcon size={20} />
            </span>
            <span className={styles.fileText}>
              <span className={styles.fileName}>{content.header.name}</span>
              <span className={styles.fileMeta}>{file.progress === null ? t('chatMessage.fileMeta', { size: formatBytes(content.header.size) }) : t('chatMessage.decryptingPercent', { percent: percent(file.progress) })}</span>
            </span>
            <button type="button" className={styles.fileButton} aria-label={t('chatMessage.downloadFile')} title={t('chatMessage.download')} onClick={() => void file.download()} disabled={file.progress !== null}>
              <DownloadIcon size={18} />
            </button>
          </div>
        ) : null}
      </div>

      {hasTools ? (
        <div className={styles.tools} role="toolbar" aria-label={t('chatMessage.actions')}>
          {content.kind === 'text' ? (
            <>
              <button
                type="button"
                className={styles.tool}
                aria-label={revealed ? t('common.hide') : t('common.show')}
                title={revealed ? t('common.hide') : t('common.show')}
                onClick={() => {
                  setChoice({ whenHidden: hidden, revealed: !revealed });
                }}
              >
                {revealed ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
              </button>
              <button type="button" className={[styles.tool, copied ? styles.toolDone : ''].join(' ')} aria-label={copied ? t('common.copied') : t('common.copy')} title={copied ? undefined : t('common.copy')} onClick={() => void copy()}>
                {copied ? <CheckIcon size={17} className={styles.toolCheck} /> : <CopyIcon size={17} />}
              </button>
              <span className="visually-hidden" role="status">
                {copied ? t('common.copied') : ''}
              </span>
            </>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className={[styles.tool, styles.toolDanger].join(' ')}
              aria-label={t('chatMessage.delete')}
              title={t('chatMessage.deleteTitle')}
              onClick={() => {
                onDelete(message.id);
              }}
            >
              <TrashIcon size={17} />
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
